import type { AppModule } from '../system/app';
import { ENABLE_CLAIM_PAYMENT_APPROVAL_FLAG } from './claim-authority';
import { isLiteEnabledOrgRole } from './lite';
import { ADMIN_ROLES, type AdminRole, ORG_ROLES, type OrgRole } from './user';

/**
 * Section an organisation role is listed under in role pickers (CUR-1420). Every role declares
 * one, so a new role cannot be added without deciding where it appears.
 */
export type OrgRoleGroup = 'console' | 'modules' | 'portals';

/** Section an admin console role is listed under in role pickers (CUR-1420). */
export type AdminRoleGroup = 'platform' | 'management' | 'operations';

/** The feature flags a role can wait on (CUR-2675). */
export type RoleFeatureFlag = typeof ENABLE_CLAIM_PAYMENT_APPROVAL_FLAG;

/**
 * The external portals (CUR-1987). A member holding a portal role and no console-workspace role is
 * an external party confined to the portal: the server refuses them every procedure not opened to
 * that portal (`meta.portalAccess`, `portalConfinementAllows` in `permissions/roles`).
 */
export const EXTERNAL_PORTALS = ['fleet', 'supplier'] as const;
export type ExternalPortal = (typeof EXTERNAL_PORTALS)[number];

interface RoleMetadataFields {
    /** Modules that must be enabled for this role to be visible/assignable */
    requiredModules?: AppModule[];
    /**
     * A feature flag that must be on for the organisation before the role can be given (CUR-2675).
     * `canAssignRole` offers it only when the caller passes the flag as on, and every server-side role
     * write refuses adding it while the flag is off; someone who already holds it keeps it.
     */
    requiredFlag?: RoleFeatureFlag;
    /** If true, only org_admin or super_admin can assign this role */
    requiresAdminToAssign?: boolean;
    /**
     * Companion roles that must always accompany this role (CUR-846). Selecting
     * this role auto-selects them, and every server-side role write normalises
     * the selection through `withRequiredRoles`, so a membership can never hold
     * this role without its companions.
     */
    requiresRoles?: OrgRole[];
    /** Human-readable description of what this role grants access to */
    description: string;
}

/**
 * A role in the `portals` section must name the external portal it opens, and no other role may
 * (CUR-1987), so a new external role cannot reach the server without the portal confinement
 * knowing about it.
 */
export type RoleMetadata = RoleMetadataFields &
    (
        | {
              /** Section this role is listed under in role pickers */
              group: Exclude<OrgRoleGroup, 'portals'>;
              portal?: never;
          }
        | {
              group: 'portals';
              /** The external portal this role opens; its holder is confined to it unless they also hold a console role. */
              portal: ExternalPortal;
          }
    );

export interface AdminRoleMetadata {
    /** Section this role is listed under in role pickers */
    group: AdminRoleGroup;
    /** This role is only valid in the admin console (orgId = -1) */
    isAdminConsoleRole: true;
    /** Only super_admin can assign this role */
    requiresSuperAdminToAssign: boolean;
    /** Human-readable description of what this role grants access to */
    description: string;
}

/**
 * Metadata for organisation roles including descriptions and assignment requirements.
 */
export const ROLE_METADATA: Record<OrgRole, RoleMetadata> = {
    [ORG_ROLES.ORG_ADMIN]: {
        group: 'console',
        requiresAdminToAssign: true,
        description: 'Full administrative access including user management and organisation settings.',
    },
    [ORG_ROLES.USER_MANAGER]: {
        group: 'console',
        // A user manager is always a standard user as well — user_manager on its
        // own cannot access any console workspace (CUR-846).
        requiresRoles: [ORG_ROLES.USER],
        description: 'Invite team members and assign roles. Cannot modify organisation admins.',
    },
    [ORG_ROLES.USER]: {
        group: 'console',
        description: 'Standard access to create records and update records they own.',
    },
    [ORG_ROLES.SUPPLIER]: {
        group: 'portals',
        portal: 'supplier',
        description: 'External supplier portal access.',
    },
    [ORG_ROLES.AUTO_DETECT_USER]: {
        group: 'modules',
        requiredModules: ['auto-detect'],
        requiresAdminToAssign: true,
        description: 'Operate the auto-detect AI inbox and triage detected vulnerabilities, including sensitive types.',
    },
    [ORG_ROLES.SHARED_EDIT_USER]: {
        group: 'console',
        description: 'Can edit records assigned to their handling parties, enabling team collaboration.',
    },
    [ORG_ROLES.ATTESTATIONS_USER]: {
        group: 'modules',
        requiredModules: ['attestations'],
        requiresAdminToAssign: true,
        description: 'Create and manage user attestation records and compliance declarations.',
    },
    [ORG_ROLES.WEBSITE_CHECKER_USER]: {
        group: 'modules',
        requiredModules: ['website-checker'],
        requiresAdminToAssign: true,
        description: 'Access to website compliance monitoring and checking tools.',
    },
    [ORG_ROLES.MARKET_PDS_USER]: {
        group: 'modules',
        requiredModules: ['pds-compare'],
        requiresAdminToAssign: true,
        description: 'Access to PDS comparison and analysis tools.',
    },
    [ORG_ROLES.TRAINING_MANAGER]: {
        group: 'modules',
        requiredModules: ['compliance'],
        requiresAdminToAssign: true,
        description:
            'Full access to add and view training records for employees in assigned training manager handling parties.',
    },
    [ORG_ROLES.FLEET_PORTAL_USER]: {
        group: 'portals',
        portal: 'fleet',
        requiredModules: ['fleet'],
        requiresAdminToAssign: true,
        description:
            'External fleet-manager access to the fleet portal, scoped to the fleet clients they are a member of, with repair authorisation decisions. Grants no console record creation or editing.',
    },
    [ORG_ROLES.FMC_COORDINATOR]: {
        group: 'portals',
        portal: 'fleet',
        requiredModules: ['fleet'],
        requiresAdminToAssign: true,
        description:
            'Read-only fleet portal access across every client under the consolidator they coordinate. Never approves, declines or queries a repair authorisation.',
    },
    [ORG_ROLES.COMPLIANCE_AUDIT_MANAGER]: {
        group: 'modules',
        requiredModules: ['compliance-audit'],
        requiresAdminToAssign: true,
        description:
            'Full access to the compliance audit tool: create and run audits, answer questions, override results and manage notes and findings.',
    },
    [ORG_ROLES.COMPLIANCE_AUDIT_READER]: {
        group: 'modules',
        requiredModules: ['compliance-audit'],
        requiresAdminToAssign: true,
        description:
            'Read-only access to the compliance audit tool: view audits, evidence, insights and exports without changing them.',
    },
    [ORG_ROLES.ACCOUNTS]: {
        group: 'console',
        requiredModules: ['claim'],
        requiresAdminToAssign: true,
        requiredFlag: ENABLE_CLAIM_PAYMENT_APPROVAL_FLAG,
        description:
            'Sees every claim read only, including notes and communications. Gives the final approval on payments and marks them paid.',
    },
};

/**
 * Metadata for admin console roles.
 * All admin roles are restricted to the admin console (orgId = -1)
 * and can only be assigned by super_admin.
 */
export const ADMIN_ROLE_METADATA: Record<AdminRole, AdminRoleMetadata> = {
    [ADMIN_ROLES.SUPER_ADMIN]: {
        group: 'platform',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description: 'Unrestricted access across the admin console.',
    },
    [ADMIN_ROLES.ADMIN_READ_ONLY]: {
        group: 'platform',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description: 'Read-only access to all admin console modules. Cannot create, update, or delete data.',
    },
    [ADMIN_ROLES.ADMIN_ORG_MANAGER]: {
        group: 'management',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description: 'Manage organisations, onboarding, and ownership settings.',
    },
    [ADMIN_ROLES.ADMIN_USER_MANAGER]: {
        group: 'management',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description: 'Invite and manage users, roles, and status.',
    },
    [ADMIN_ROLES.ADMIN_CONTENT_MANAGER]: {
        group: 'management',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description: 'Manage content, FAQs, and communication assets.',
    },
    [ADMIN_ROLES.ADMIN_COMMS_MANAGER]: {
        group: 'management',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description: 'Control outbound comms, notifications, and templates.',
    },
    [ADMIN_ROLES.ADMIN_AI_MANAGER]: {
        group: 'management',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description: 'Configure AI models, pricing, and usage controls.',
    },
    [ADMIN_ROLES.ADMIN_SYSTEM_OPS]: {
        group: 'operations',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description: 'Run maintenance tasks, health checks, and upgrades.',
    },
    [ADMIN_ROLES.ADMIN_OPS_READER]: {
        group: 'operations',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description:
            'Read-only cross-org ops debugging via the ops-read MCP: user accounts, email envelopes, entity and audit state.',
    },
    [ADMIN_ROLES.ADMIN_SUPPORT_MANAGER]: {
        group: 'operations',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description: 'Request and manage support sessions for temporary access to organisations.',
    },
    [ADMIN_ROLES.ADMIN_SUPPORT_USER]: {
        group: 'operations',
        isAdminConsoleRole: true,
        requiresSuperAdminToAssign: true,
        description:
            'Support staff account that can be granted temporary access to customer organisations via support sessions.',
    },
};

// ============================================================================
// AI Assistant Context - Extended metadata for AI chat assistance
// ============================================================================

/**
 * Extended role context for AI assistant to understand user capabilities.
 * Used to help the AI provide role-appropriate guidance and suggestions.
 */
export interface RoleAIContext {
    /** Human-readable display title for the role */
    title: string;
    /** Detailed description of the role's purpose and scope */
    description: string;
    /** List of key capabilities this role grants */
    capabilities: string[];
}

/**
 * AI-focused context for organisation roles.
 * Provides detailed capability information for the AI assistant.
 */
export const ROLE_AI_CONTEXT: Partial<Record<OrgRole | AdminRole, RoleAIContext>> = {
    // Organisation roles
    [ORG_ROLES.ORG_ADMIN]: {
        title: 'Organisation Administrator',
        description:
            'Full administrative access to the organisation with control over all records, users, and settings.',
        capabilities: [
            'View and manage all claims, incidents, complaints, and obligations',
            'Invite and manage team members, assign roles',
            'Configure organisation settings, templates, and workflows',
            'Access billing, wallet, and financial information',
            'View audit trails and generate reports',
            'Manage communication templates and signatures',
            'Configure risk categories, controls, and appetites',
            'Approve support session requests',
        ],
    },
    [ORG_ROLES.USER]: {
        title: 'Standard User',
        description: 'Access to create and manage records within assigned scope.',
        capabilities: [
            'Create new claims, incidents, and complaints',
            'View and update records you own or are assigned to your handling parties',
            'Create and manage your own tasks',
            'View FAQs and help documentation',
            'Use the AI assistant for guidance',
            'Access training records',
        ],
    },
    [ORG_ROLES.USER_MANAGER]: {
        title: 'User Manager',
        description: 'Team management capabilities for inviting and managing users.',
        capabilities: [
            'Invite new team members to the organisation',
            'Assign and modify user roles (except organisation admins)',
            'View and manage user accounts and status',
        ],
    },
    [ORG_ROLES.SHARED_EDIT_USER]: {
        title: 'Collaborative User',
        description: 'Enhanced editing access for team collaboration on shared records.',
        capabilities: [
            'All standard user capabilities',
            'Edit records assigned to your handling parties (not just records you own)',
            'Collaborate on shared incidents, claims, and complaints',
            'Update controls and risks assigned to your handling parties',
        ],
    },
    [ORG_ROLES.SUPPLIER]: {
        title: 'External Supplier',
        description: 'Limited portal access for external service providers.',
        capabilities: ['Access supplier portal features', 'View assigned supplier jobs'],
    },
    [ORG_ROLES.AUTO_DETECT_USER]: {
        title: 'Email Auto-Detect User',
        description: 'Operate the auto-detect AI inbox and triage detected vulnerabilities, including sensitive types.',
        capabilities: [
            'Use the email auto-detect dashboard',
            'Review and action AI-classified emails',
            'Review, skip, rerun and delete classified messages',
            'View and manage vulnerability detections, including sensitive types (family violence, severe mental health, bereavement)',
        ],
    },
    [ORG_ROLES.WEBSITE_CHECKER_USER]: {
        title: 'Website Checker User',
        description: 'Access to website compliance monitoring and scanning tools.',
        capabilities: [
            'Run website compliance scans',
            'View compliance reports and findings',
            'Configure scan parameters and schedules',
        ],
    },
    [ORG_ROLES.ATTESTATIONS_USER]: {
        title: 'Attestations User',
        description: 'Manage user attestation and compliance declaration processes.',
        capabilities: [
            'Create and send attestation requests to users',
            'View and manage attestation responses',
            'Track attestation compliance status',
            'Configure attestation forms',
        ],
    },

    [ORG_ROLES.TRAINING_MANAGER]: {
        title: 'Training Manager',
        description: 'Manage training records for employees within assigned training manager handling parties.',
        capabilities: [
            'View and manage training records for employees in assigned handling parties',
            'Create training records for employees in assigned handling parties',
            'View training period data and staff training compliance',
            'Generate staff training reports scoped to assigned handling parties',
        ],
    },
    [ORG_ROLES.COMPLIANCE_AUDIT_MANAGER]: {
        title: 'Compliance Audit Manager',
        description: 'Full access to the compliance audit tool.',
        capabilities: [
            'Create compliance audits and upload evidence files',
            'Run, re-run and continue audits, and answer the audit questions',
            'Override step results, re-assess sub-steps and acknowledge recommendations',
            'Add, edit and delete audit notes, and dismiss or restore findings',
            'View audit insights, statistics, activity and exports',
        ],
    },
    [ORG_ROLES.COMPLIANCE_AUDIT_READER]: {
        title: 'Compliance Audit Reader',
        description: 'Read-only access to the compliance audit tool.',
        capabilities: [
            'View compliance audits, steps, evidence files and notes',
            'View audit insights, statistics and activity',
            'Export an audit as a PDF',
            'Cannot create, run, answer, override or annotate audits',
        ],
    },
    [ORG_ROLES.ACCOUNTS]: {
        title: 'Accounts',
        description: 'Every claim read only, and the final approval and payment of claim payments.',
        capabilities: [
            'View every claim, including its notes, communications, documents and financials',
            'Give the final approval on payments when the organisation uses accounts approval',
            'Mark payments as paid',
            'Cannot create or change claims, reserves or payments',
        ],
    },

    // Admin console roles
    [ADMIN_ROLES.SUPER_ADMIN]: {
        title: 'System Administrator',
        description: 'Platform-wide administrative access across the admin console.',
        capabilities: [
            'Full access to the Admin Console',
            'Manage all organisations on the platform',
            'Configure system-wide settings and defaults',
            'Manage all users across organisations',
            'Access all admin modules (billing, AI, system ops, etc.)',
        ],
    },
};

/**
 * Check if a role can be assigned given the context
 * @param role - The role to check
 * @param options - Context for checking (enabled modules, admin status and the session's effective Lite tier)
 * @returns true if the role can be assigned
 */
export function canAssignRole(
    role: OrgRole,
    options: {
        enabledModules: AppModule[];
        isAdmin: boolean;
        isLiteOrg: boolean;
        /** The role flags on for the organisation (CUR-2675). A role waiting on one not listed is not offered. */
        enabledRoleFlags?: readonly RoleFeatureFlag[];
    },
): boolean {
    const metadata = ROLE_METADATA[role];

    // Curium Lite (CUR-2686): only the roles on the Lite allow-list
    if (options.isLiteOrg && !isLiteEnabledOrgRole(role)) return false;

    // Check feature flag requirements: hidden unless the caller says the flag is on
    if (metadata.requiredFlag !== undefined && !options.enabledRoleFlags?.includes(metadata.requiredFlag)) {
        return false;
    }

    // Check module requirements
    if (metadata.requiredModules?.length) {
        const hasRequiredModules = metadata.requiredModules.every((mod) => options.enabledModules.includes(mod));
        if (!hasRequiredModules) return false;
    }

    // Check admin requirement
    if (metadata.requiresAdminToAssign && !options.isAdmin) {
        return false;
    }

    return true;
}

/**
 * Expand a role selection with the companion roles each selected role requires
 * (e.g. `user_manager` ⇒ `user`), transitively. Order is preserved; required
 * roles are appended. Admin-console roles pass through untouched.
 *
 * Every server-side role write (invite, update, bulk import) must run the
 * selection through this before persisting — the client auto-select is UX only.
 */
export function withRequiredRoles<T extends OrgRole | AdminRole>(roles: readonly T[]): (T | OrgRole)[] {
    const result: (T | OrgRole)[] = [...new Set(roles)];
    const seen = new Set<string>(result);
    for (let i = 0; i < result.length; i++) {
        const required = ROLE_METADATA[result[i] as OrgRole]?.requiresRoles ?? [];
        for (const role of required) {
            if (!seen.has(role)) {
                seen.add(role);
                result.push(role);
            }
        }
    }
    return result;
}

/**
 * Roles in `selected` that cannot be deselected because another selected role
 * requires them (e.g. `user` is locked while `user_manager` is selected).
 */
export function getLockedRequiredRoles(selected: readonly OrgRole[]): Set<OrgRole> {
    const locked = new Set<OrgRole>();
    for (const role of selected) {
        for (const required of ROLE_METADATA[role]?.requiresRoles ?? []) {
            if (required !== role) locked.add(required);
        }
    }
    return locked;
}

/**
 * The roles `after` adds to `before` that wait on a feature flag (CUR-2675), with the flag each
 * waits on. A role already held is never refused again: someone keeps a role whose flag was later
 * turned off.
 */
export function getAddedRolesRequiringFlags(
    before: readonly string[],
    after: readonly OrgRole[],
): { role: OrgRole; flag: RoleFeatureFlag }[] {
    return after.flatMap((role) => {
        const flag = ROLE_METADATA[role]?.requiredFlag;
        return flag !== undefined && !before.includes(role) ? [{ role, flag }] : [];
    });
}

/**
 * The only roles a Service API token may give or take away (invite and update). A token never
 * changes `org_admin` or any other role outside this list, and never edits a member who holds
 * `org_admin` (CUR-912): organisation administration stays with the people the organisation made
 * administrators. Companion roles (`withRequiredRoles`) of these roles are inside the list too.
 */
export const SERVICE_API_ASSIGNABLE_ROLES = [
    ORG_ROLES.USER,
    ORG_ROLES.USER_MANAGER,
    ORG_ROLES.SUPPLIER,
    ORG_ROLES.ATTESTATIONS_USER,
] as const satisfies readonly OrgRole[];

/**
 * Roles in the selection whose `requiredModules` are not all enabled — the module half of
 * `canAssignRole` on its own, for callers that are already admin-equivalent (Service API tokens
 * have org-wide access, so `requiresAdminToAssign` does not apply to them).
 */
export function getRolesBlockedByModules(roles: readonly OrgRole[], enabledModules: readonly string[]): OrgRole[] {
    return roles.filter((role) => {
        const required = ROLE_METADATA[role].requiredModules;
        return !!required?.length && !required.every((mod) => enabledModules.includes(mod));
    });
}

/**
 * Filter roles that can be assigned in the given context
 * @param roles - Array of roles to filter
 * @param options - Context for filtering (enabled modules, admin status and the session's effective Lite tier)
 * @returns Filtered array of assignable roles
 */
export function getAssignableRoles(
    roles: OrgRole[],
    options: {
        enabledModules: AppModule[];
        isAdmin: boolean;
        isLiteOrg: boolean;
        enabledRoleFlags?: readonly RoleFeatureFlag[];
    },
): OrgRole[] {
    return roles.filter((role) => canAssignRole(role, options));
}
